import { useEffect, useState } from 'react'
import { cn } from 'components/UI/cn'
import { DropdownMenuTrigger } from '@radix-ui/react-dropdown-menu'
import type { FilterStore } from 'components/Filters/FilterStore'
import {
  DropdownMenu,
  DropdownMenuContent,
} from 'components/UI/BaseDropdown/dropdown-base'
import { DatePicker } from 'components/FormUtils/DatePicker'
import FbButton from 'components/FbUI/FbButton'
import { FilterButton } from '../components/FilterButton'
import { FilterCloseButton } from '../components/FilterCloseButton'
import { useRegisterFilterKey } from '../components/FilterModal'

export function DateRangeFilter({
  title,
  filterKey,
  filterStore,
}: {
  title: string
  filterKey: string
  filterStore: FilterStore
}) {
  const [open, setOpen] = useState(false)
  const {
    pendingRangeFilters,
    setPendingRangeFilters,
    clearPendingRangeFilter,
    registerFilterTitle,
  } = filterStore()
  const current = pendingRangeFilters?.[filterKey!]

  // Note: RangeFilter type uses number, but date filters use string values (YYYY-MM-DD)
  // This is a pre-existing type inconsistency in the FilterStore
  const [minDate, setMinDate] = useState<string | undefined>(
    current?.min != null ? String(current.min) : undefined
  )
  const [maxDate, setMaxDate] = useState<string | undefined>(
    current?.max != null ? String(current.max) : undefined
  )
  const [error, setError] = useState<string | null>(null)

  // Sync local state when current filter changes
  useEffect(() => {
    setMinDate(current?.min != null ? String(current.min) : undefined)
    setMaxDate(current?.max != null ? String(current.max) : undefined)
  }, [current?.min, current?.max])

  function validateDates(): boolean {
    if (minDate && maxDate) {
      const min = new Date(minDate)
      const max = new Date(maxDate)
      if (min > max) {
        setError('End date must be ≥ start date')
        return false
      }
    }
    setError(null)
    return true
  }

  function saveFilters() {
    if (!validateDates()) return

    if (minDate || maxDate) {
      // RangeFilter type expects number but date filters store strings (YYYY-MM-DD)
      // This cast maintains backward compatibility with the existing FilterStore API
      setPendingRangeFilters(
        filterKey,
        minDate as unknown as number | undefined,
        maxDate as unknown as number | undefined
      )
    }

    setOpen(false)
  }

  const clearFilters = () => {
    setMinDate(undefined)
    setMaxDate(undefined)
    setError(null)
    clearPendingRangeFilter(filterKey)
  }

  const isRangeSet = current?.min != null || current?.max != null

  // Compact display label used on the trigger pill when a range is set —
  // formats applied dates as "Jan 5 – Feb 12" / "≥ Jan 5" / "≤ Feb 12" so
  // the pill carries the value at a glance instead of the field name.
  const formatShort = (raw: string) => {
    const d = new Date(raw)
    if (Number.isNaN(d.getTime())) return raw
    const sameYear = d.getFullYear() === new Date().getFullYear()
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: sameYear ? undefined : '2-digit',
    })
  }
  const triggerLabel = (() => {
    const min = current?.min != null ? String(current.min) : null
    const max = current?.max != null ? String(current.max) : null
    if (!min && !max) return title
    if (min && !max) return `${title}: ≥ ${formatShort(min)}`
    if (!min && max) return `${title}: ≤ ${formatShort(max)}`
    return `${title}: ${formatShort(min!)} – ${formatShort(max!)}`
  })()

  useEffect(() => {
    registerFilterTitle(filterKey, title)
  }, [])

  useRegisterFilterKey(filterKey)

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          // Rebuild local state from the committed filter on each open so
          // a previously-cancelled edit doesn't linger. Mirrors the same
          // fix in SelectFilter/SingleSelectFilter.
          setMinDate(current?.min != null ? String(current.min) : undefined)
          setMaxDate(current?.max != null ? String(current.max) : undefined)
          setError(null)
        }
        setOpen(nextOpen)
      }}
    >
      <div className={'relative'}>
        <DropdownMenuTrigger asChild>
          <FilterButton
            open={open}
            isActive={isRangeSet}
            title={triggerLabel}
          />
        </DropdownMenuTrigger>
        <FilterCloseButton
          isActive={isRangeSet}
          open={open}
          onClick={() => clearFilters()}
        />
      </div>

      <DropdownMenuContent
        forceMount={true}
        side={'bottom'}
        className={cn(
          '!rounded-xl !border-neutral-200 !shadow-lg !bg-white w-[280px] !p-3'
        )}
        sideOffset={6}
      >
        <div className="flex gap-3 flex-col">
          <DatePicker
            label="Start Date"
            value={minDate}
            onChange={(value) => {
              setMinDate(value || undefined)
              setError(null)
            }}
            allowTyping
          />
          <DatePicker
            label="End Date"
            value={maxDate}
            onChange={(value) => {
              setMaxDate(value || undefined)
              setError(null)
            }}
            allowTyping
          />
          {error && (
            <p className="m-0 text-xs leading-4 text-error-strong">{error}</p>
          )}
        </div>

        <div className="mt-4 flex items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              setMinDate(undefined)
              setMaxDate(undefined)
              setError(null)
            }}
            disabled={!minDate && !maxDate}
            className={cn(
              'text-sm font-medium transition-colors',
              !minDate && !maxDate
                ? 'text-neutral-400 cursor-not-allowed'
                : 'text-brand-active hover:text-brand-bright'
            )}
          >
            Reset
          </button>
          <div className="flex gap-2">
            <FbButton
              variant="secondary"
              type="button"
              onClick={() => setOpen(false)}
            >
              Cancel
            </FbButton>
            <FbButton
              type="button"
              className="!bg-brand-bright !text-white hover:!bg-brand-active active:!bg-brand-active disabled:!bg-neutral-100 disabled:!text-neutral-400 disabled:!border-neutral-100 disabled:hover:!bg-neutral-100"
              onClick={saveFilters}
              // Stays enabled when a filter is currently applied so Reset
              // doesn't trap the user — they can clear the inputs and
              // Apply the empty state to drop the filter from the store.
              disabled={
                !minDate &&
                !maxDate &&
                current?.min == null &&
                current?.max == null
              }
            >
              Apply
            </FbButton>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
